import * as React from "react";
import { DataTable } from "components/DataTable/DataTable";
import { DataTableRowAction } from "components/DataTable/types";
import { ApiError } from "gen/clients";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import { ClientPromptItem } from "../AiPromptsTab";
import { ConfirmationDialog } from "components/ConfirmationDialog/ConfirmationDialog";
import { useDialogState } from "hooks/useDialogState";

interface Props {
  prompts: ClientPromptItem[];
  onEditClick: (prompt: ClientPromptItem) => void;
  onResetClick: (prompt: ClientPromptItem) => Promise<void>;
  isUpdating: boolean;
  updateError: ApiError | null;
  canEdit: boolean;
}

const ClientAiPromptsTable: React.FC<Props> = ({
  prompts,
  onEditClick,
  onResetClick,
  isUpdating,
  updateError,
  canEdit
}) => {
  const [confirmResetOpen, openConfirmReset, closeConfirmReset] = useDialogState();
  const [promptToReset, setPromptToReset] = React.useState<ClientPromptItem | null>(null);

  const truncateText = (text: string, maxLength = 100): string => {
    if (text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + "...";
  };

  const handleResetClick = React.useCallback(
    (prompt: ClientPromptItem) => {
      setPromptToReset(prompt);
      openConfirmReset();
    },
    [openConfirmReset]
  );

  const handleConfirmReset = React.useCallback(async () => {
    if (promptToReset) {
      await onResetClick(promptToReset);
      closeConfirmReset();
      setPromptToReset(null);
    }
  }, [promptToReset, onResetClick, closeConfirmReset]);

  const handleCancelReset = React.useCallback(() => {
    closeConfirmReset();
    setPromptToReset(null);
  }, [closeConfirmReset]);

  const rowActions = React.useCallback(
    (prompt: ClientPromptItem): DataTableRowAction[] => {
      const actions: DataTableRowAction[] = [
        {
          title: prompt.isOverridden ? "Edit Override" : "Create Override",
          action: () => onEditClick(prompt),
          disabled: !canEdit
        }
      ];

      if (prompt.isOverridden) {
        actions.push({
          title: "Reset to Default",
          action: () => handleResetClick(prompt),
          color: "warning.main",
          disabled: !canEdit
        });
      }

      return actions;
    },
    [canEdit, onEditClick, handleResetClick]
  );

  return (
    <>
      <DataTable
        columns={[
          {
            id: "name",
            title: "Prompt Name",
            cell: (prompt: ClientPromptItem) => (
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  {prompt.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {prompt.description}
                </Typography>
              </Box>
            )
          },
          {
            id: "status",
            title: "Status",
            cell: (prompt: ClientPromptItem) => (
              <Chip
                label={prompt.isOverridden ? "Override" : "Default"}
                size="small"
                color={prompt.isOverridden ? "primary" : "default"}
                variant={prompt.isOverridden ? "filled" : "outlined"}
              />
            )
          },
          {
            id: "value",
            title: "Prompt Text",
            cell: (prompt: ClientPromptItem) => {
              const displayValue = prompt.isOverridden ? prompt.overrideValue || "" : prompt.defaultValue;
              return (
                <Tooltip title={displayValue} placement="top-start">
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.875rem",
                      whiteSpace: "pre-wrap",
                      cursor: "help"
                    }}
                  >
                    {truncateText(displayValue, 150)}
                  </Typography>
                </Tooltip>
              );
            }
          }
        ]}
        data={prompts}
        isLoading={isUpdating}
        error={updateError && <ApiErrorMessage apiError={updateError} />}
        rowKey={(prompt: ClientPromptItem) => prompt.key}
        rowActions={rowActions}
        dense={false}
      />

      {confirmResetOpen && promptToReset && (
        <ConfirmationDialog
          title="Reset to Default"
          content={`Are you sure you want to reset "${promptToReset.name}" to the system default value? The client-level override will be removed.`}
          onConfirm={handleConfirmReset}
          onClose={handleCancelReset}
        />
      )}
    </>
  );
};

export { ClientAiPromptsTable };
